// src/pages/sessions/components/LiveSession/Controls/MacScreenShareGuide.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon,
  CursorArrowRaysIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { isSafari } from '@/utils/platform';

const MacScreenShareGuide = ({ isOpen, onClose, onContinue }) => {
  const { t } = useTranslation();
  const showSafariWarning = isSafari();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg shadow-2xl border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-600 rounded-lg">
              <ComputerDesktopIcon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-white">
              {t('macScreenGuide.title', 'Share Your Screen')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white transition-colors rounded"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-gray-300 text-sm">
            {t('macScreenGuide.checkBefore', 'Before you share, please check:')}
          </p>

          {/* Step 1: Screen Recording Permission */}
          <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-1.5 bg-cyan-600/20 rounded-lg flex-shrink-0">
                <ShieldCheckIcon className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-medium text-sm mb-2">
                  {t('macScreenGuide.permission.title', 'Screen Recording Permission')}
                </h3>
                <p className="text-gray-400 text-sm mb-3">
                  {t('macScreenGuide.permission.description', 'Your browser needs permission to record your screen. If this is your first time:')}
                </p>
                <ul className="space-y-1.5 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    {t('macScreenGuide.permission.step1', 'Go to System Settings → Privacy & Security')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    {t('macScreenGuide.permission.step2', 'Click "Screen Recording"')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    {t('macScreenGuide.permission.step3', 'Enable your browser (Chrome/Firefox/Safari)')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    {t('macScreenGuide.permission.step4', 'You may need to restart your browser')}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Step 2: What Happens Next */}
          <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-1.5 bg-cyan-600/20 rounded-lg flex-shrink-0">
                <CursorArrowRaysIcon className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-medium text-sm mb-2">
                  {t('macScreenGuide.whatHappens.title', 'What Happens Next')}
                </h3>
                <p className="text-gray-400 text-sm mb-3">
                  {t('macScreenGuide.whatHappens.description', 'After clicking "Continue", you\'ll see a system dialog where you can choose:')}
                </p>
                <ul className="space-y-1.5 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    {t('macScreenGuide.whatHappens.option1', 'Your entire screen')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    {t('macScreenGuide.whatHappens.option2', 'A specific application window')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    {t('macScreenGuide.whatHappens.option3', 'A browser tab (Chrome only)')}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Safari Warning */}
          {showSafariWarning && (
            <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-yellow-400 font-medium text-sm mb-1">
                    {t('macScreenGuide.safariWarning.title', 'Safari Users')}
                  </h3>
                  <p className="text-yellow-200/80 text-sm">
                    {t('macScreenGuide.safariWarning.message', 'Safari has limited screen sharing support. For the best experience, we recommend using Chrome or Firefox.')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium text-sm"
          >
            {t('macScreenGuide.cancel', 'Cancel')}
          </button>
          <button
            onClick={onContinue}
            className="flex-1 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors font-medium text-sm"
          >
            {t('macScreenGuide.continue', 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MacScreenShareGuide;
